import type { Route, Routes } from '@angular/router';

/**
 * Starts downloading the lazy chunks the first navigation needs (the main layout, the feature's
 * route config, the page component) while the session probe is still in flight. The router later
 * calls the same `loadComponent` / `loadChildren` functions and gets the modules the browser has
 * already loaded, so only the timing changes: boot no longer waits for `me`, then `auth/csrf`, then
 * each chunk in turn.
 *
 * Mirrors the router's matching for the plain configs this app uses (static and `:param` segments,
 * `''` parents, `pathMatch: 'full'`); redirects and custom matchers are not followed. A wrong guess
 * only downloads a chunk early.
 */
export function warmInitialRoute(routes: Routes, url: string): void {
  const segments = url
    .split(/[?#;]/)[0]
    .split('/')
    .filter(Boolean)
    .map(segment => decodeURIComponent(segment));
  void warm(routes, segments).catch(() => undefined);
}

/** Segments `route.path` consumes from the front of `segments`, or `null` when it does not match. */
export function consumedSegments(route: Route, segments: readonly string[]): number | null {
  if (route.matcher || route.redirectTo !== undefined) return null;
  const parts = (route.path ?? '').split('/').filter(Boolean);
  if (parts.length > segments.length) return null;
  for (let i = 0; i < parts.length; i++) {
    if (!parts[i].startsWith(':') && parts[i] !== segments[i]) return null;
  }
  if (route.pathMatch === 'full' && parts.length !== segments.length) return null;
  return parts.length;
}

async function warm(routes: Routes, segments: readonly string[]): Promise<boolean> {
  for (const route of routes) {
    const consumed = consumedSegments(route, segments);
    if (consumed === null) continue;
    const rest = segments.slice(consumed);
    const hasChildren = Boolean(route.children || route.loadChildren);
    // A leaf must consume every segment, as in the router.
    if (!hasChildren && rest.length > 0) continue;
    const component = route.loadComponent?.();
    // The router reports a failed chunk itself; the early copy must not add an unhandled rejection.
    if (component instanceof Promise) component.catch(() => undefined);
    const children = route.children ?? (route.loadChildren ? await loadChildRoutes(route) : null);
    if (!children) return true;
    if (await warm(children, rest)) return true;
  }
  return false;
}

async function loadChildRoutes(route: Route): Promise<Routes | null> {
  const loaded: unknown = await route.loadChildren!();
  if (Array.isArray(loaded)) return loaded;
  const fromDefault = (loaded as { default?: unknown } | null)?.default;
  return Array.isArray(fromDefault) ? fromDefault : null;
}
